import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { scrollToBrief } from "@/lib/scroll";

const EASE = [0.22, 1, 0.36, 1];

const LINES = ["Scroll-stopping", "video ads —", "before you pay."];

const MaskedLine = ({ children, delay }) => (
  <span className="hero-mask">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 0.9, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const PRICE_PILLS = ["15s — $250", "30s — $350", "Longer — custom"];

export const Hero = () => {
  return (
    <section id="top" className="relative overflow-hidden pt-28 sm:pt-32">
      <div className="pointer-events-none absolute inset-0 hairline-grid opacity-60" />
      <div className="pointer-events-none absolute -top-40 left-1/2 h-[560px] w-[560px] -translate-x-1/2 rounded-full bg-white/[0.04] blur-[130px]" />

      <div className="relative mx-auto max-w-6xl px-5 pb-16 sm:px-8 lg:pb-20">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="mb-6 inline-flex items-center gap-2 rounded-full border border-zinc-800 bg-white/[0.03] px-4 py-1.5"
          data-testid="hero-badge"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-[#C7FF3C]" />
          <span className="ff-mono text-[11px] uppercase tracking-[0.25em] text-zinc-400">
            First sample free · in your inbox within 24h
          </span>
        </motion.div>

        <h1
          className="font-display text-4xl font-extrabold leading-[0.95] tracking-tight sm:text-6xl lg:text-7xl"
          data-testid="hero-headline"
        >
          {LINES.map((l, i) => (
            <MaskedLine key={i} delay={0.25 + i * 0.12}>
              {l}
            </MaskedLine>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.7 }}
          className="mt-6 max-w-2xl text-base leading-relaxed text-zinc-400 sm:text-lg"
          data-testid="hero-sub"
        >
          Short-form motion ads that stop the scroll and turn viewers into buyers — for DTC
          and B2B SaaS brands across IG, TikTok & YouTube. No filming, no studios.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.82 }}
          className="mt-6 flex flex-wrap gap-2.5"
          data-testid="hero-price-pills"
        >
          {PRICE_PILLS.map((p) => (
            <span
              key={p}
              className="rounded-full border border-zinc-800 bg-white/[0.02] px-4 py-2 ff-mono text-xs text-zinc-300"
            >
              {p}
            </span>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.95 }}
          className="mt-8 flex flex-col items-start gap-4 sm:flex-row sm:items-center"
        >
          <button
            onClick={scrollToBrief}
            data-testid="hero-cta"
            className="group inline-flex items-center gap-2 rounded-full bg-[#C7FF3C] px-8 py-4 text-sm font-semibold text-black transition-colors hover:bg-[#b4ec2a]"
          >
            See a free sample before you pay
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <span className="ff-mono text-xs text-zinc-500">No card. No commitment.</span>
        </motion.div>
      </div>
    </section>
  );
};
